import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { themeConfig } from '../../../environments/theme.config';

export type ThemeName = 'default' | 'violet' | 'custom';

export interface ThemeOption {
  value: ThemeName;
  label: string;
  description: string;
}

export interface CustomThemeColors {
  primaryColor: string;
  primaryHoverColor: string;
  sidebarBg: string;
  sidebarLinkHoverBg: string;
  pageBg: string;
}

export interface CustomThemeConfigOverride {
  colors: CustomThemeColors;
  appLogoPath: string;
  loginLogoPath: string;
  allowedEmailDomains: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  isSidebarClosed = new BehaviorSubject<boolean>(true);
  private readonly storageKey = 'aquila-selected-theme';
  private readonly customThemeStorageKey =
    'aquila-custom-theme-config-override';
  private readonly allowedDomainsStorageKey = 'aquila-allowed-email-domains';
  private readonly appLogoPathStorageKey = 'aquila-app-logo-path';
  private readonly loginLogoPathStorageKey = 'aquila-login-logo-path';
  private readonly fallbackTheme: ThemeName = themeConfig.defaultTheme;
  private readonly defaultAppLogoPath = themeConfig.themes.default.appLogoPath;
  private readonly defaultLoginLogoPath =
    themeConfig.themes.default.loginLogoPath;
  private readonly appLogoPath$$ = new BehaviorSubject<string>(
    this.readStoredAppLogoPath()
  );
  private readonly loginLogoPath$$ = new BehaviorSubject<string>(
    this.readStoredLoginLogoPath()
  );
  private readonly defaultCustomConfig: CustomThemeConfigOverride = {
    appLogoPath: themeConfig.themes.custom.appLogoPath,
    loginLogoPath: themeConfig.themes.custom.loginLogoPath,
    allowedEmailDomains: [...themeConfig.themes.custom.allowedEmailDomains],
    colors: {
      primaryColor: themeConfig.themes.custom.colors.primaryColor,
      primaryHoverColor: themeConfig.themes.custom.colors.primaryHoverColor,
      sidebarBg: themeConfig.themes.custom.colors.sidebarBg,
      sidebarLinkHoverBg: themeConfig.themes.custom.colors.sidebarLinkHoverBg,
      pageBg: themeConfig.themes.custom.colors.pageBg,
    },
  };
  private readonly defaultCustomColors: CustomThemeColors = {
    primaryColor: themeConfig.themes.custom.colors.primaryColor,
    primaryHoverColor: themeConfig.themes.custom.colors.primaryHoverColor,
    sidebarBg: themeConfig.themes.custom.colors.sidebarBg,
    sidebarLinkHoverBg: themeConfig.themes.custom.colors.sidebarLinkHoverBg,
    pageBg: themeConfig.themes.custom.colors.pageBg,
  };

  readonly themeOptions: ThemeOption[] = [
    {
      value: 'default',
      label: 'Default Theme',
      description: 'Mobily-inspired blue palette',
    },
    {
      value: 'violet',
      label: 'Violet Theme',
      description: 'Purple stc-inspired palette',
    },
    {
      value: 'custom',
      label: 'Custom Theme',
      description: 'Pick your own colors',
    },
  ];
  readonly appLogoPath$ = this.appLogoPath$$.asObservable();
  readonly loginLogoPath$ = this.loginLogoPath$$.asObservable();

  initializeTheme(): void {
    const savedTheme = this.getSavedTheme();
    this.appLogoPath$$.next(this.resolveAppLogoPath(savedTheme));
    this.loginLogoPath$$.next(this.resolveLoginLogoPath(savedTheme));
    this.applyTheme(savedTheme);
    this.applyThemeColors(savedTheme);
  }

  setTheme(theme: ThemeName): void {
    this.applyTheme(theme);
    localStorage.setItem(this.storageKey, theme);
    this.appLogoPath$$.next(this.resolveAppLogoPath(theme));
    this.loginLogoPath$$.next(this.resolveLoginLogoPath(theme));
    this.applyThemeColors(theme);
  }

  getCurrentTheme(): ThemeName {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    return this.isThemeName(currentTheme) ? currentTheme : this.fallbackTheme;
  }

  getCustomThemeColors(): CustomThemeColors {
    const customOverride = this.getCustomThemeConfigOverride();
    if (!customOverride) {
      return { ...this.defaultCustomColors };
    }

    return {
      primaryColor: this.sanitizeHexColor(customOverride.colors.primaryColor),
      primaryHoverColor: this.sanitizeHexColor(
        customOverride.colors.primaryHoverColor,
        this.defaultCustomColors.primaryHoverColor
      ),
      sidebarBg: this.sanitizeHexColor(customOverride.colors.sidebarBg),
      sidebarLinkHoverBg: this.sanitizeHexColor(
        customOverride.colors.sidebarLinkHoverBg,
        this.defaultCustomColors.sidebarLinkHoverBg
      ),
      pageBg: this.sanitizeHexColor(customOverride.colors.pageBg),
    };
  }

  setCustomThemeColors(colors: CustomThemeColors): void {
    const sanitizedColors: CustomThemeColors = {
      primaryColor: this.sanitizeHexColor(colors.primaryColor),
      primaryHoverColor: this.sanitizeHexColor(
        colors.primaryHoverColor,
        this.defaultCustomColors.primaryHoverColor
      ),
      sidebarBg: this.sanitizeHexColor(colors.sidebarBg),
      sidebarLinkHoverBg: this.sanitizeHexColor(
        colors.sidebarLinkHoverBg,
        this.defaultCustomColors.sidebarLinkHoverBg
      ),
      pageBg: this.sanitizeHexColor(colors.pageBg),
    };

    const customConfig = this.getResolvedCustomThemeConfig();
    customConfig.colors = sanitizedColors;
    localStorage.setItem(
      this.customThemeStorageKey,
      JSON.stringify(customConfig)
    );

    if (this.getCurrentTheme() === 'custom') {
      this.applyThemeColors('custom');
    }
  }

  getAppLogoPath(): string {
    return this.appLogoPath$$.value;
  }

  setAppLogoPath(path: string): void {
    const customConfig = this.getResolvedCustomThemeConfig();
    const sanitizedPath = path.trim() || this.defaultCustomConfig.appLogoPath;
    customConfig.appLogoPath = sanitizedPath;
    localStorage.setItem(
      this.customThemeStorageKey,
      JSON.stringify(customConfig)
    );
    if (this.getCurrentTheme() === 'custom') {
      this.appLogoPath$$.next(sanitizedPath);
    }
  }

  getLoginLogoPath(): string {
    return this.loginLogoPath$$.value;
  }

  setLoginLogoPath(path: string): void {
    const customConfig = this.getResolvedCustomThemeConfig();
    const sanitizedPath = path.trim() || this.defaultCustomConfig.loginLogoPath;
    customConfig.loginLogoPath = sanitizedPath;
    localStorage.setItem(
      this.customThemeStorageKey,
      JSON.stringify(customConfig)
    );
    if (this.getCurrentTheme() === 'custom') {
      this.loginLogoPath$$.next(sanitizedPath);
    }
  }

  getAllowedEmailDomains(): string[] {
    const storedDomains = this.readStoredAllowedEmailDomains();
    if (storedDomains.length > 0) {
      return storedDomains;
    }

    const theme = this.getCurrentTheme();
    if (theme === 'custom') {
      return this.getResolvedCustomThemeConfig().allowedEmailDomains;
    }
    return this.sanitizeDomainList(
      themeConfig.themes[theme].allowedEmailDomains
    );
  }

  setAllowedEmailDomains(input: string | string[]): void {
    const sanitizedDomains = this.parseAllowedDomains(input);
    const customConfig = this.getResolvedCustomThemeConfig();
    customConfig.allowedEmailDomains = sanitizedDomains;
    localStorage.setItem(
      this.customThemeStorageKey,
      JSON.stringify(customConfig)
    );
    localStorage.setItem(
      this.allowedDomainsStorageKey,
      JSON.stringify(sanitizedDomains)
    );
  }

  getThemeConfigOverride(): CustomThemeConfigOverride {
    return this.getResolvedCustomThemeConfig();
  }

  // Backward-compatible wrappers for previous callers.
  getLogoPath(): string {
    return this.getAppLogoPath();
  }

  setLogoPath(path: string): void {
    this.setAppLogoPath(path);
  }

  private getSavedTheme(): ThemeName {
    const savedTheme = localStorage.getItem(this.storageKey);
    return this.isThemeName(savedTheme) ? savedTheme : this.fallbackTheme;
  }

  private applyTheme(theme: ThemeName): void {
    document.documentElement.setAttribute('data-theme', theme);
  }

  private isThemeName(value: string | null): value is ThemeName {
    return value === 'default' || value === 'violet' || value === 'custom';
  }

  private applyCustomThemeColors(colors: CustomThemeColors): void {
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty('--stc-color', colors.primaryColor);
    rootStyle.setProperty('--stc-color-hover', colors.primaryHoverColor);
    rootStyle.setProperty('--theme-sidebar-bg', colors.sidebarBg);
    rootStyle.setProperty(
      '--theme-sidebar-link-hover-bg',
      colors.sidebarLinkHoverBg
    );
    rootStyle.setProperty('--theme-page-bg', colors.pageBg);
  }

  private applyThemeColors(theme: ThemeName): void {
    const colors =
      theme === 'custom'
        ? this.getCustomThemeColors()
        : themeConfig.themes[theme].colors;
    this.applyCustomThemeColors(colors);
  }

  private clearCustomThemeVariables(): void {
    const rootStyle = document.documentElement.style;
    rootStyle.removeProperty('--stc-color');
    rootStyle.removeProperty('--stc-color-hover');
    rootStyle.removeProperty('--theme-sidebar-bg');
    rootStyle.removeProperty('--theme-sidebar-link-hover-bg');
    rootStyle.removeProperty('--theme-page-bg');
  }

  private sanitizeHexColor(
    value: string | undefined,
    fallback = '#000000'
  ): string {
    if (!value) {
      return fallback;
    }

    const trimmed = value.trim();
    return /^#[0-9A-Fa-f]{6}$/.test(trimmed) ? trimmed : fallback;
  }

  private readStoredAppLogoPath(): string {
    return this.resolveAppLogoPath(this.getSavedTheme());
  }

  private readStoredLoginLogoPath(): string {
    return this.resolveLoginLogoPath(this.getSavedTheme());
  }

  private resolveAppLogoPath(theme: ThemeName): string {
    if (theme === 'custom') {
      return this.getResolvedCustomThemeConfig().appLogoPath;
    }
    return themeConfig.themes[theme].appLogoPath || this.defaultAppLogoPath;
  }

  private resolveLoginLogoPath(theme: ThemeName): string {
    if (theme === 'custom') {
      return this.getResolvedCustomThemeConfig().loginLogoPath;
    }
    return themeConfig.themes[theme].loginLogoPath || this.defaultLoginLogoPath;
  }

  private getCustomThemeConfigOverride(): CustomThemeConfigOverride | null {
    const rawValue = localStorage.getItem(this.customThemeStorageKey);
    if (!rawValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawValue) as Partial<CustomThemeConfigOverride>;
      return {
        appLogoPath:
          typeof parsed.appLogoPath === 'string'
            ? parsed.appLogoPath.trim() || this.defaultCustomConfig.appLogoPath
            : this.defaultCustomConfig.appLogoPath,
        loginLogoPath:
          typeof parsed.loginLogoPath === 'string'
            ? parsed.loginLogoPath.trim() ||
              this.defaultCustomConfig.loginLogoPath
            : this.defaultCustomConfig.loginLogoPath,
        allowedEmailDomains: this.sanitizeDomainList(
          parsed.allowedEmailDomains ?? this.readStoredAllowedEmailDomains()
        ),
        colors: {
          primaryColor:
            parsed.colors?.primaryColor ??
            this.defaultCustomConfig.colors.primaryColor,
          primaryHoverColor:
            parsed.colors?.primaryHoverColor ??
            this.defaultCustomConfig.colors.primaryHoverColor,
          sidebarBg:
            parsed.colors?.sidebarBg ??
            this.defaultCustomConfig.colors.sidebarBg,
          sidebarLinkHoverBg:
            parsed.colors?.sidebarLinkHoverBg ??
            this.defaultCustomConfig.colors.sidebarLinkHoverBg,
          pageBg:
            parsed.colors?.pageBg ?? this.defaultCustomConfig.colors.pageBg,
        },
      };
    } catch {
      return null;
    }
  }

  private getResolvedCustomThemeConfig(): CustomThemeConfigOverride {
    const override = this.getCustomThemeConfigOverride();
    if (!override) {
      return {
        ...this.defaultCustomConfig,
        colors: { ...this.defaultCustomConfig.colors },
        allowedEmailDomains: [...this.defaultCustomConfig.allowedEmailDomains],
      };
    }

    return {
      ...override,
      colors: { ...override.colors },
      allowedEmailDomains: [...override.allowedEmailDomains],
    };
  }

  private parseAllowedDomains(input: string | string[]): string[] {
    if (Array.isArray(input)) {
      return this.sanitizeDomainList(input);
    }

    return this.sanitizeDomainList(
      input
        .split(',')
        .map((domain) => domain.trim())
        .filter(Boolean)
    );
  }

  private readStoredAllowedEmailDomains(): string[] {
    const storedValue = localStorage.getItem(this.allowedDomainsStorageKey);
    if (!storedValue) {
      return [];
    }

    try {
      const parsed = JSON.parse(storedValue);
      return this.sanitizeDomainList(parsed);
    } catch {
      return [];
    }
  }

  private sanitizeDomainList(value: unknown): string[] {
    const domains = Array.isArray(value)
      ? value
          .filter((domain): domain is string => typeof domain === 'string')
          .map((domain) => domain.trim())
      : [];

    const normalized = domains
      .map((domain) => this.normalizeDomain(domain))
      .filter(Boolean) as string[];

    return normalized.length > 0
      ? Array.from(new Set(normalized))
      : [...this.defaultCustomConfig.allowedEmailDomains];
  }

  private normalizeDomain(domain: string): string | null {
    const trimmed = domain.trim().toLowerCase();
    if (!trimmed) {
      return null;
    }

    if (trimmed.startsWith('@') && /^@[a-z0-9.-]+\.[a-z]{2,}$/.test(trimmed)) {
      return trimmed;
    }

    if (/^[a-z0-9.-]+\.[a-z]{2,}$/.test(trimmed)) {
      return `@${trimmed}`;
    }

    return null;
  }
}
